/**
 * @fileoverview Eurofarma Comercial Dashboard page component
 * Displays statistics, quick access buttons, and alerts for Eurofarma commercial team
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchData, intl, HTTP_STATUS } from "dmsbasereact";

import { DashboardLayout } from "../../components/Dashboard";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { APP_COLORS } from "../../theme/appTheme";

// MUI Icons
import VaccinesIcon from "@mui/icons-material/Vaccines";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import BusinessIcon from "@mui/icons-material/Business";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PeopleIcon from "@mui/icons-material/People";

/**
 * EurofarmaComercialDashboard - Dashboard page for Eurofarma commercial team
 * @returns {JSX.Element} The Eurofarma commercial dashboard with stats, quick access, and alerts
 */
export const EurofarmaComercialDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        stats: null,
        alerts: null,
        userName: null,
    });

    /**
     * Fetch all dashboard data from API endpoints
     */
    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsResponse, alertsResponse] = await Promise.all([
                fetchData("/dashboard/eurofarma/stats", "GET", true),
                fetchData("/dashboard/eurofarma/alerts", "GET", true),
            ]);

            const newData = { stats: null, alerts: null, userName: null };

            if (statsResponse.status === HTTP_STATUS.OK_200) {
                newData.stats = statsResponse.data?.data;
                newData.userName = statsResponse.data?.data?.user_name;
            }

            if (alertsResponse.status === HTTP_STATUS.OK_200) {
                newData.alerts = alertsResponse.data?.data;
            }

            setDashboardData(newData);
        } catch (err) {
            console.error("Error fetching eurofarma commercial dashboard data:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    /**
     * Format pending returns alerts
     */
    const formatPendingReturnsAlerts = () => {
        if (!dashboardData.alerts?.pending_returns) return [];
        return dashboardData.alerts.pending_returns.map((item) => ({
            id: item.return_pk,
            title: item.organization,
            description: `${item.quantity} ${intl.get("Capsules").d("capsules")}`,
            return_pk: item.return_pk,
        }));
    };

    /**
     * Handle click on pending return alert
     */
    const handleReturnClick = (item) => {
        if (item?.return_pk) {
            navigate(APP_ROUTES.Dispensing);
        }
    };

    // Stats configuration
    const stats = [
        {
            title: intl.get("TotalInstitutions").d("Total Institutions"),
            value: dashboardData.stats?.total_institutions || 0,
            icon: BusinessIcon,
            color: APP_COLORS.system.info,
            onClick: () => navigate(APP_ROUTES.InstitutionManagement),
        },
        {
            title: intl.get("TotalCapsules").d("Total Capsules"),
            value: dashboardData.stats?.total_capsules || 0,
            icon: LocalPharmacyIcon,
            color: APP_COLORS.secondary.main,
        },
    ];

    // Quick access buttons configuration
    const quickAccess = [
        {
            label: intl.get("SalesAnalytics").d("Sales Analytics"),
            icon: TrendingUpIcon,
            route: APP_ROUTES.Insights,
            color: APP_COLORS.primary.main,
        },
        {
            label: intl.get("InstitutionOverview").d("Institution Overview"),
            icon: BusinessIcon,
            route: APP_ROUTES.InstitutionManagement,
            color: APP_COLORS.system.info,
        },
        {
            label: intl.get("CustomerReports").d("Customer Reports"),
            icon: PeopleIcon,
            route: APP_ROUTES.Insights,
            color: APP_COLORS.system.warning,
        },
    ];

    // Alerts configuration
    const alerts = [
        {
            title: intl.get("PendingReturns").d("Pending Returns"),
            items: formatPendingReturnsAlerts(),
            severity: "warning",
            emptyMessage: intl.get("NoPendingReturns").d("No pending returns"),
            onItemClick: handleReturnClick,
        },
    ];

    return (
        <DashboardLayout
            welcomeName={dashboardData.userName}
            loading={loading}
            stats={stats}
            quickAccess={quickAccess}
            alerts={alerts}
        />
    );
};
